/**
 * Multiplication as a rectangle, and big multiplication as several rectangles.
 *
 * 24 x 37 is one rectangle, but it is also four: 20x30, 20x7, 4x30 and 4x7.
 * Seeing the big rectangle cut along the tens is the whole of long
 * multiplication, years before anyone writes it in a column.
 */

export interface Part {
  a: number;
  b: number;
  product: number;
}

/** 24 becomes [20, 4]; 137 becomes [100, 30, 7]. Zero digits contribute nothing. */
export function splitTens(n: number): number[] {
  const whole = Math.max(0, Math.floor(n));
  if (whole === 0) return [];
  const out: number[] = [];
  const digits = String(whole);
  for (let i = 0; i < digits.length; i++) {
    const value = Number(digits[i]) * 10 ** (digits.length - 1 - i);
    if (value > 0) out.push(value);
  }
  return out;
}

/**
 * Every piece of the cut-up rectangle. The products always sum to a x b — that
 * is the invariant the toy is teaching, and the one the tests pin.
 */
export function partials(a: number, b: number): Part[] {
  const out: Part[] = [];
  for (const pa of splitTens(a)) {
    for (const pb of splitTens(b)) out.push({ a: pa, b: pb, product: pa * pb });
  }
  return out;
}

/** Below this, the rectangle is drawn as individual unit squares you can count. */
export const COUNTABLE = 16;

export function shouldCount(a: number, b: number): boolean {
  return a <= COUNTABLE && b <= COUNTABLE;
}
